<script lang="ts">
	// Height of the bar (h-17); below this scroll offset the header always stays visible.
	const HEADER_HEIGHT = 68;
	// Small scroll deltas are ignored so the bar doesn't flicker on jittery touch input.
	const DIRECTION_THRESHOLD = 6;

	let scrolled = $state(false);
	let retracted = $state(false);
	let lastY = 0;

	function onScroll() {
		// Clamp against iOS rubber-band overscroll, which reports negative / past-end values.
		const maxY = document.documentElement.scrollHeight - window.innerHeight;
		const y = Math.min(Math.max(window.scrollY, 0), Math.max(maxY, 0));
		scrolled = y > 0;

		const delta = y - lastY;
		if (y <= HEADER_HEIGHT) {
			retracted = false;
		} else if (Math.abs(delta) >= DIRECTION_THRESHOLD) {
			retracted = delta > 0;
		} else {
			return;
		}
		lastY = y;
	}
</script>

<svelte:window onscroll={onScroll} />

<header class="sticky top-0 z-40 border-b" class:scrolled class:retracted>
	<div class="max-w-page mx-auto flex h-17 items-center justify-between px-2 sm:px-4">
		<a
			href="/"
			data-action="home"
			aria-label="mrt-radar.de Startseite"
			class="flex items-center gap-[10px] px-3 whitespace-nowrap"
		>
			<!-- Inline statt <img>, damit Ring und Kern beim Scrollen mit der Leiste
			     umfärben können; der grüne Blip bleibt in beiden Zuständen grün. -->
			<svg class="logo" width="24" height="24" viewBox="0 0 100 100" fill="none" aria-hidden="true">
				<circle
					class="ring"
					cx="50"
					cy="50"
					r="39"
					stroke-width="7.5"
					stroke-linecap="round"
					pathLength="100"
					stroke-dasharray="82 18"
					transform="rotate(-42 50 50)"
				></circle>
				<circle class="ring" cx="50" cy="50" r="24.5" stroke-width="3.4" opacity="0.34"></circle>
				<circle class="blip" cx="60.487873" cy="12.43666" r="5.4"></circle>
				<circle class="core" cx="50" cy="50" r="9"></circle>
			</svg>
			<span class="wordmark font-display text-[19px] font-semibold tracking-tight whitespace-nowrap"
				>mrt-radar<b class="suffix font-semibold">.de</b></span
			>
		</a>
		<nav class="flex items-center gap-[clamp(8px,2vw,26px)] px-3">
			<span class="claim px-1 py-2 text-xs font-medium sm:text-[14.5px]"
				>Schnell zum MRT-Termin</span
			>
		</nav>
	</div>
</header>

<style>
	/* Zwei Zustände: am Seitenanfang ein deckender Navy-Balken, ab dem ersten
	   Scroll Milchglas auf hellem Grund. Alle Farben wechseln mit, sonst stünde
	   weiße Schrift auf heller Fläche. */
	header {
		background-color: var(--color-bar);
		border-bottom-color: transparent;
		transition:
			background-color 0.3s,
			border-color 0.3s;
	}
	header.scrolled {
		background-color: color-mix(in oklch, var(--color-bg) 82%, transparent);
		backdrop-filter: saturate(1.4) blur(14px);
		border-bottom-color: var(--color-line);
	}

	/* Quick-return header on mobile only: slides out while scrolling down to free up
	   screen space and slides back in as soon as the user scrolls up. Desktop keeps
	   the plain sticky bar. */
	@media (width < 40rem) {
		header {
			transition:
				background-color 0.3s,
				border-color 0.3s,
				transform 0.25s ease-out;
		}
		header.retracted {
			transform: translateY(-100%);
		}
		@media (prefers-reduced-motion: reduce) {
			header {
				transition:
					background-color 0.3s,
					border-color 0.3s;
			}
		}
	}

	.logo circle {
		transition:
			stroke 0.3s,
			fill 0.3s;
	}
	.ring {
		stroke: var(--color-on-bar);
	}
	.core {
		fill: var(--color-on-bar);
	}
	.blip {
		/* Auf dem dunklen Balken braucht das Grün etwas mehr Helligkeit. */
		fill: #43c98a;
	}
	header.scrolled .ring {
		stroke: var(--color-accent);
	}
	header.scrolled .core {
		fill: var(--color-ink);
	}
	header.scrolled .blip {
		fill: var(--color-warm);
	}

	.wordmark,
	.suffix,
	.claim {
		transition: color 0.3s;
	}
	.wordmark {
		color: var(--color-on-bar);
	}
	.suffix {
		color: #43c98a;
	}
	.claim {
		color: var(--color-on-bar-soft);
	}
	header.scrolled .wordmark {
		color: var(--color-ink);
	}
	header.scrolled .suffix {
		color: var(--color-warm);
	}
	header.scrolled .claim {
		color: var(--color-ink-soft);
	}
</style>
